import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PlugDLogo from "../common/PlugDLogo";

export default function PrivacyPolicyPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors">
            <PlugDLogo size={28} className="rounded-lg" />
            <span className="font-bold text-lg">PlugD</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("legal_back_home", "Back to Home")}
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t("legal_privacy_title", "Privacy Policy")}
        </h1>
        <p className="text-sm text-gray-500 mb-10">
          {t("legal_privacy_last_updated", "Last updated: April 6, 2026")}
        </p>

        <div className="prose prose-gray max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_privacy_intro_title", "1. Introduction")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_privacy_intro_text",
                "PlugD (\"we\", \"us\", or \"our\") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our social media management platform. Please read this policy carefully. By using the Service, you consent to the practices described in this policy."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_privacy_collect_title", "2. Information We Collect")}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {t(
                "legal_privacy_collect_account",
                "Account Information: When you register, we collect your name, email address, and password. If you sign up through a third-party service, we may receive your name and email from that provider."
              )}
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              {t(
                "legal_privacy_collect_social",
                "Social Media Account Data: When you connect social media accounts, we collect and store OAuth access tokens, your social media profile information (username, profile picture, follower counts), and metadata necessary to publish content and retrieve analytics on your behalf."
              )}
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              {t(
                "legal_privacy_collect_content",
                "Content Data: We store the content you create, schedule, or publish through the Service, including text, images, videos, and associated metadata such as scheduling times and target platforms."
              )}
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              {t(
                "legal_privacy_collect_usage",
                "Usage Data: We automatically collect information about how you interact with the Service, including pages visited, features used, timestamps, browser type, device information, and IP address."
              )}
            </p>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_privacy_collect_analytics",
                "Analytics Data: When you use our analytics features, we retrieve performance data from connected social media platforms, including engagement metrics, impressions, reach, and audience demographics."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_privacy_use_title", "3. How We Use Your Information")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_privacy_use_text",
                "We use the collected information to: provide and maintain the Service; publish and schedule content to your connected social media accounts; generate analytics and reports on your social media performance; improve and personalize the Service; provide AI-powered content suggestions and optimization; communicate with you about the Service, including updates and support; process payments and manage subscriptions; detect and prevent fraud, abuse, and security issues; and comply with legal obligations."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_privacy_sharing_title", "4. Information Sharing and Disclosure")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_privacy_sharing_text",
                "We do not sell your personal information. We may share your information with: social media platforms as necessary to provide the Service (publishing content, retrieving analytics); third-party service providers who assist us in operating the Service (hosting, payment processing, email delivery); AI service providers for content generation features (content is processed but not stored by these providers); and law enforcement or government authorities when required by law. When you publish content through the Service, that content becomes public on the respective social media platforms according to their privacy settings."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_privacy_security_title", "5. Data Security")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_privacy_security_text",
                "We implement industry-standard security measures to protect your information, including encryption of data in transit (TLS/SSL) and at rest, secure storage of OAuth tokens and credentials, regular security audits and monitoring, and access controls limiting who can view your data. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_privacy_retention_title", "6. Data Retention")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_privacy_retention_text",
                "We retain your account information and content for as long as your account is active. When you disconnect a social media account, we delete the associated access tokens within 30 days. When you delete your PlugD account, we delete your personal data within 90 days, except where retention is required by law. Aggregated and anonymized data may be retained indefinitely for analytical purposes."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_privacy_rights_title", "7. Your Rights")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_privacy_rights_text",
                "You have the right to: access the personal data we hold about you; request correction of inaccurate data; request deletion of your data; export your data in a portable format; withdraw consent for data processing; object to processing of your data; and disconnect social media accounts at any time. To exercise these rights, contact us at privacy@plugd.online or use the relevant features within the Service settings."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_privacy_cookies_title", "8. Cookies and Tracking")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_privacy_cookies_text",
                "We use essential cookies to maintain your session and authentication state. We may use analytics cookies to understand how users interact with the Service. You can control cookie settings through your browser preferences. Disabling essential cookies may prevent you from using certain features of the Service."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_privacy_third_party_title", "9. Third-Party Services")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_privacy_third_party_text",
                "The Service integrates with third-party social media platforms and services. Each platform has its own privacy policy governing how they handle your data. We encourage you to review the privacy policies of Facebook/Meta, Instagram, Twitter/X, LinkedIn, TikTok, YouTube, Pinterest, and any other platforms you connect. We are not responsible for the privacy practices of these third-party services."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_privacy_children_title", "10. Children&apos;s Privacy")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_privacy_children_text",
                "The Service is not intended for users under the age of 16. We do not knowingly collect personal information from children. If we become aware that a child has provided us with personal information, we will take steps to delete such information promptly."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_privacy_changes_title", "11. Changes to This Policy")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_privacy_changes_text",
                "We may update this Privacy Policy from time to time. We will notify you of material changes by email or through a notice in the Service. Your continued use of the Service after changes are posted constitutes acceptance of the updated policy."
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {t("legal_privacy_contact_title", "12. Contact Us")}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t(
                "legal_privacy_contact_text",
                "If you have any questions about this Privacy Policy or our data practices, please contact us at privacy@plugd.online."
              )}
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()} {t("land_footer_copyright", "PlugD. All rights reserved.")}
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-sm text-gray-700 font-medium">
              {t("legal_privacy_title", "Privacy Policy")}
            </Link>
            <Link to="/terms" className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
              {t("legal_tos_title", "Terms of Service")}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
